'use client';

import { type Key, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '@workspace/lib';
import { tabsVariants } from '@workspace/ui/common/Tabs/tabs.variants';

import { Button } from '../Buttons';

export interface TabItem {
  key: Key;
  label: ReactNode;
}

export interface TabsProps {
  items: readonly TabItem[];
  activeIndex: number;
  onChange: (index: number) => void;
  idPrefix: string;
  panelId: string;
  ariaLabel: string;
}

export function Tabs({ items, activeIndex, onChange, idPrefix, panelId, ariaLabel }: TabsProps) {
  const selectTab = (index: number, moveFocus = false) => {
    onChange(index);
    if (moveFocus) document.getElementById(`${idPrefix}-tab-${index}`)?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | undefined;

    if (event.key === 'ArrowRight') nextIndex = (index + 1) % items.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + items.length) % items.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = items.length - 1;

    if (nextIndex === undefined) return;

    event.preventDefault();
    selectTab(nextIndex, true);
  };

  if (!items.length) return null;

  const selectedIndex = Math.min(Math.max(activeIndex, 0), items.length - 1);
  const { list } = tabsVariants();

  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('scrollbar-hide flex w-full overflow-x-auto', list())}>
      {items.map((item, index) => {
        const isActive = index === selectedIndex;

        return (
          <Button
            key={item.key}
            variant="text"
            overrideClasses
            id={`${idPrefix}-tab-${index}`}
            role="tab"
            aria-selected={isActive}
            aria-controls={panelId}
            tabIndex={index === selectedIndex ? 0 : -1}
            onClick={() => selectTab(index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'shrink-0 cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-offset-2',
              tabsVariants({ isActive }).tab(),
            )}
          >
            {item.label}
          </Button>
        );
      })}
    </div>
  );
}
